/**
 * Content type classifications used across the application.
 *
 * This is the **single source of truth** for content type values.
 * Both `classification-testing` and `content-search` domains re-export
 * from here to avoid duplication.
 *
 * @remarks
 * The GraphQL schema defines a separate `ContentType` enum with different
 * casing (`Image`, `Text`), so we cannot re-export from codegen types.
 * This enum uses lowercase values to match the API response format.
 *
 * @module ContentType
 */

/**
 * Enum of valid content types for classification and content search.
 * Use enum members (e.g. `ContentType.TEXT`) for runtime switch/case comparisons.
 */
export enum ContentType {
  TEXT = 'text',
  IMAGE = 'image',
  AUDIO = 'audio',
  MIXED = 'mixed',
  USERNAME = 'username',
}

/**
 * Union type derived from the {@link ContentType} enum values.
 * Use this when only a type-level union is needed (e.g. interface fields).
 */
export type ContentTypeValue = `${ContentType}`;

/**
 * Array of all valid content type values for runtime validation checks.
 * Use this to guard `as ContentType` casts on untrusted API data.
 */
export const CONTENT_TYPES: readonly ContentTypeValue[] = Object.values(ContentType);

/**
 * Safely coerce an unknown string to a {@link ContentTypeValue}.
 * Defaults to `'text'` for unknown or missing values to maintain
 * backward compatibility with pre-existing content handling.
 *
 * @param value - The string value to validate
 * @returns A valid content type value, or `'text'` as fallback
 */
export const toContentType = (value: string | undefined | null): ContentTypeValue =>
  CONTENT_TYPES.includes(value as ContentTypeValue) ? (value as ContentTypeValue) : 'text';
